import { useState, FormEvent, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authService, LoginRequest, setToken } from '@shared/services/api';
import AuthLayout from '@shared/layouts/AuthLayout';
import { Card } from '@shared/components/ui/Card';
import { Button } from '@shared/components/ui/Button';
import { Input } from '@shared/components/ui/Input';
import { CircleAlert } from 'lucide-react';

interface MFAStep {
  requiresMFA: boolean;
  email: string;
  password: string;
}

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation(['auth', 'errors', 'common']);
  const [formData, setFormData] = useState<LoginRequest>({
    email: '',
    password: '',
    totp_code: '',
  });
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [mfaStep, setMfaStep] = useState<MFAStep | null>(null);
  const [rememberMe, setRememberMe] = useState(false);
  const [totpCode, setTotpCode] = useState('');

  useEffect(() => {
    // Check if there's a success message from email verification
    const params = new URLSearchParams(location.search);
    const message = params.get('message');
    const sessionExpired = params.get('session_expired');
    const emailFromUrl = params.get('email');

    // Also check navigation state
    const state = location.state as { session_expired?: boolean; from?: Location } | null;

    if (message === 'verified') {
      setError('');
      setSuccessMessage(t('login.emailVerified'));
      // Preencher email se vier da verificação
      if (emailFromUrl) {
        setFormData((prev) => ({ ...prev, email: emailFromUrl }));
      }
    } else if (sessionExpired === 'true' || state?.session_expired) {
      setSuccessMessage('');
      setError(t('auth:errors.sessionExpired'));
    } else {
      setSuccessMessage('');
      setError('');
    }
  }, [location.search, location.state, t]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (loading) return;
    setError('');
    setSuccessMessage('');
    setLoading(true);

    try {
      // If we're in MFA step, include TOTP code
      const loginData: any = mfaStep
        ? {
            email: mfaStep.email,
            password: mfaStep.password,
            totp_code: totpCode,
            remember_me: rememberMe,
          }
        : {
            ...formData,
            remember_me: rememberMe,
          };

      const response = await authService.login(loginData);

      // Check if MFA is required
      if ('requires_mfa' in response && response.requires_mfa) {
        setMfaStep({
          requiresMFA: true,
          email: formData.email,
          password: formData.password,
        });
        setLoading(false);
        return;
      }

      // Login successful
      if ('token' in response) {
        setToken(response.token);

        const pendingInvitationToken = sessionStorage.getItem('pending_invitation_token');
        if (pendingInvitationToken) {
          sessionStorage.removeItem('pending_invitation_token');
          navigate(`/accept-invitation?token=${encodeURIComponent(pendingInvitationToken)}`, {
            replace: true,
          });
          return;
        }
        sessionStorage.removeItem('pending_invitation_token');

        // Check if user was redirected from a protected route
        const state = location.state as { from?: Location } | null;
        const redirectTo = state?.from
          ? `${state.from.pathname}${state.from.search}${state.from.hash}`
          : '/dashboard';

        navigate(redirectTo, { replace: true });
      }
    } catch (err: any) {
      const errorCode = String(err.response?.data?.error || '').toLowerCase();
      const isMfaError = Boolean(mfaStep) && /totp|mfa/.test(errorCode);

      if (
        errorCode.includes('invalid_credentials') ||
        errorCode.includes('invalid email or password')
      ) {
        setError(t('auth:errors.invalidCredentials'));
      } else if (isMfaError) {
        setError(t('auth:errors.invalidMfa'));
      } else if (errorCode.includes('email_not_verified')) {
        setError(t('auth:errors.emailNotVerified'));
      } else if (errorCode.includes('account_locked')) {
        setError(
          t('auth:errors.accountLocked', { minutes: err.response?.data?.details?.minutes ?? '' })
        );
      } else {
        setError(t('common:errors.generic'));
      }

      if (!isMfaError) setMfaStep(null);
      setTotpCode('');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelMFA = () => {
    setMfaStep(null);
    setTotpCode('');
    setError('');
  };

  return (
    <AuthLayout>
      <Card className="p-8">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">
            {mfaStep ? t('mfa.title') : t('login.title')}
          </h2>
          <p className="mt-2 text-sm text-text-muted-light dark:text-text-muted-dark font-medium">
            {mfaStep ? t('login.mfaRequired') : t('login.subtitle')}
          </p>
        </div>
        {successMessage && (
          <div
            role="status"
            aria-live="polite"
            className="mb-6 rounded-xl border-l-4 border-state-ok-light bg-state-ok-bg-light p-4 text-sm font-semibold text-state-ok-light dark:border-state-ok-dark dark:bg-state-ok-bg-dark dark:text-state-ok-dark"
          >
            {successMessage}
          </div>
        )}
        <form className="space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div
              role="alert"
              aria-live="assertive"
              className="bg-state-danger-bg-light dark:bg-state-danger-bg-dark border-l-4 border-state-danger-light dark:border-state-danger-dark p-4 rounded-xl"
            >
              <div className="flex">
                <div className="flex-shrink-0">
                  <CircleAlert
                    aria-hidden="true"
                    className="h-5 w-5 text-state-danger-light dark:text-state-danger-dark"
                  />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-bold text-state-danger-light dark:text-state-danger-dark">
                    {error}
                  </p>
                </div>
              </div>
            </div>
          )}
          {!mfaStep && (
            <>
              <div className="space-y-4">
                <Input
                  label={t('login.emailLabel')}
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  disabled={loading}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder={t('login.emailPlaceholder')}
                />
                <div className="space-y-2">
                  <div className="flex items-center justify-between px-1">
                    <label htmlFor="password" className="app-field-label mb-0">
                      {t('login.passwordLabel')}
                    </label>
                    <Link
                      to="/forgot-password"
                      className="min-h-11 inline-flex items-center text-[10px] font-black uppercase tracking-widest text-brand-600 transition-colors hover:text-brand-700 dark:text-brand-400"
                    >
                      {t('login.forgotPassword')}
                    </Link>
                  </div>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={formData.password}
                    disabled={loading}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="•••••"
                  />
                  <div className="pt-1">
                    <label className="group flex min-h-11 cursor-pointer items-center gap-3">
                      <input
                        type="checkbox"
                        checked={rememberMe}
                        disabled={loading}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        className="h-5 w-5 shrink-0 cursor-pointer accent-brand-600"
                      />
                      <span className="text-xs font-bold text-text-muted-light transition-colors group-hover:text-slate-700 dark:text-text-muted-dark dark:group-hover:text-slate-300">
                        {t('login.rememberMe')}
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </>
          )}
          {mfaStep && (
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
              <label
                htmlFor="totp"
                className="block text-xs font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark mb-3 text-center"
              >
                {t('mfa.mfaCodeLabel')}
              </label>
              <input
                id="totp"
                name="totp"
                type="text"
                inputMode="numeric"
                maxLength={6}
                pattern="[0-9]{6}"
                required
                value={totpCode}
                disabled={loading}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="block w-full px-4 py-6 bg-surface-card-light dark:bg-surface-card-dark border border-border-strong-light dark:border-border-strong-dark rounded-2xl text-slate-900 dark:text-white text-center text-4xl font-black tracking-[0.4em] focus:outline-none focus:ring-2 focus:ring-brand-500 transition-all"
                placeholder={t('mfa.mfaPlaceholder')}
                autoFocus
              />
              <p className="mt-4 text-[10px] font-bold text-text-subtle-light dark:text-text-subtle-dark text-center uppercase tracking-wider">
                {t('mfa.codeLabel')}
              </p>
            </div>
          )}
          <div className="pt-2">
            <Button
              type="submit"
              className="w-full py-4"
              isLoading={loading}
              disabled={!!(mfaStep && totpCode.length !== 6)}
            >
              {loading
                ? t('login.submitting')
                : mfaStep
                  ? t('mfa.submitButton')
                  : t('login.submitButton')}
            </Button>
            {mfaStep && (
              <button
                type="button"
                onClick={handleCancelMFA}
                disabled={loading}
                className="mt-4 min-h-11 w-full text-center py-2 px-4 text-xs font-black uppercase tracking-widest text-text-subtle-light dark:text-text-subtle-dark hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
              >
                {t('mfa.cancel')}
              </button>
            )}
          </div>
          {!mfaStep && (
            <div className="text-center mt-8">
              <p className="text-sm font-medium text-text-muted-light dark:text-text-muted-dark">
                {t('login.noAccount')}{' '}
                <Link
                  to="/register"
                  className="font-black text-brand-600 dark:text-brand-400 hover:underline"
                >
                  {t('login.signUp')}
                </Link>
              </p>
            </div>
          )}
        </form>
      </Card>
    </AuthLayout>
  );
}
